import { byPrefixAndName } from '@awesome.me/kit-f16c5d7bca/icons'
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  HStack,
  IconButton,
  Link,
  List,
  ListItem,
  Tooltip
} from '@chakra-ui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import PropTypes from 'prop-types'
import { useMemo } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

import { useMFNav } from './MFNav.Provider'

/**
 * Default props for navigation items, applied consistently across different components.
 *
 * @param {Object} param - The object containing navigation item details.
 * @param {string} param.title - The title of the navigation item.
 * @returns {Object} Default properties for a navigation item.
 */
const defaultProps = ({ title }) => ({
  variant: 'ghost',
  colorScheme: 'fg',
  'aria-label': title,
  fontSize: 'sm',
  justifyContent: 'flex-start',
  gap: 2,
  h: 10
})

// Shared PropTypes across multiple navigation components to ensure consistency.
const sharedPropTypes = {
  id: PropTypes.string,
  title: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  state: PropTypes.oneOf(['full', 'dock']) /** @default 'full */
}

const activeLinkStyles = {
  bg: 'neutral.50',
  color: 'fg !important',

  _hover: {
    bg: 'neutral.50 !important',
    color: 'fg !important'
  }
}

const NavItemLabel = ({ title, showExternalIndicator = false }) => (
  <HStack spacing={2}>
    <Box as="span">{title}</Box>
    {showExternalIndicator && <FontAwesomeIcon size="xs" icon={byPrefixAndName['fas']['arrow-up-right-from-square']} />}
  </HStack>
)

NavItemLabel.propTypes = {
  title: PropTypes.string.isRequired,
  showExternalIndicator: PropTypes.bool
}

/**
 * ExternalNavItem component renders an external link navigation item.
 *
 * This component conditionally renders either a full-width button or an icon-only button based on the `state` prop.
 *
 * @param {Object} props - The properties for the component.
 * @param {string} props.id - The unique identifier for the navigation item.
 * @param {string} props.title - The title of the navigation item.
 * @param {React.ReactNode} props.icon - The icon associated with the navigation item.
 * @param {string} props.href - The URL for the external link.
 * @param {'full' | 'dock'} [props.state='full'] - The state of the navigation (full or docked).
 * @returns {JSX.Element} The rendered external navigation item.
 */
const ExternalNavItem = ({ id, title, icon, href, state = 'full', showExternalIndicator = false }) =>
  state === 'full' ? (
    <Button
      {...defaultProps({ title })}
      key={id}
      leftIcon={
        <HStack w={4} h={4}>
          {icon}
        </HStack>
      }
      w="100%"
    >
      <Link isExternal href={href} color="inherit !important" _hover={{ color: 'inherit !important' }}>
        <NavItemLabel title={title} showExternalIndicator={showExternalIndicator} />
      </Link>
    </Button>
  ) : (
    <IconButton
      {...defaultProps({ title })}
      key={id}
      as={Link}
      icon={icon}
      isExternal
      href={href}
      justifyContent="center"
    />
  )

ExternalNavItem.propTypes = {
  ...sharedPropTypes,
  href: PropTypes.string.isRequired,
  showExternalIndicator: PropTypes.bool
}

/**
 * InternalNavItemWithoutChildren component renders an internal navigation item without children.
 *
 * It renders either a full-width button or an icon-only button based on the `state` prop.
 * The component uses `NavLink` to handle internal routing within the application.
 *
 * @param {Object} props - The properties for the component.
 * @param {string} props.id - The unique identifier for the navigation item.
 * @param {string} props.title - The title of the navigation item.
 * @param {React.ReactNode} props.icon - The icon associated with the navigation item.
 * @param {string} props.to - The URL for the internal link.
 * @param {'full' | 'dock'} [props.state='full'] - The state of the navigation (full or docked).
 * @returns {JSX.Element} The rendered internal navigation item without children.
 */
const InternalNavItemWithoutChildren = ({ id, title, icon, to, state = 'full' }) => {
  const { pathname } = useLocation()

  if (state !== 'full')
    return (
      <IconButton
        {...defaultProps({ title })}
        key={id}
        as={NavLink}
        aria-current={pathname === to ? 'page' : undefined}
        exact
        icon={icon}
        to={to || ''}
        justifyContent="center"
        // _activeLink={activeLinkStyles}
        sx={to && pathname.startsWith(to) ? activeLinkStyles : {}}
        isDisabled={!to}
      />
    )

  return (
    <Tooltip key={id} hidden={!!to} label="Coming soon" aria-label={title} placement="right">
      <Box w="full">
        <Button
          {...defaultProps({ title })}
          as={NavLink}
          aria-current={pathname === to ? 'page' : undefined}
          exact
          leftIcon={
            <HStack w={4} h={4}>
              {icon}
            </HStack>
          }
          w="100%"
          to={to || ''}
          // _activeLink={activeLinkStyles}
          sx={to && pathname.startsWith(to) ? activeLinkStyles : {}}
          isDisabled={!to}
        >
          {title}
        </Button>
      </Box>
    </Tooltip>
  )
}

InternalNavItemWithoutChildren.propTypes = {
  ...sharedPropTypes,
  to: PropTypes.string
}

const SidebarLeafNavItem = ({ item }) => {
  if (item.externalLink || item.newTab) {
    return (
      <ExternalNavItem
        id={item.id}
        title={item.title}
        icon={item.icon}
        href={item.navLink}
        showExternalIndicator={item.newTab}
      />
    )
  }

  return <InternalNavItemWithoutChildren id={item.id} title={item.title} icon={item.icon} to={item.navLink} />
}

SidebarLeafNavItem.propTypes = {
  item: PropTypes.shape({
    ...sharedPropTypes,
    navLink: PropTypes.string,
    externalLink: PropTypes.bool,
    newTab: PropTypes.bool
  }).isRequired
}

/**
 * InternalNavItemWithChildrenButton component renders a button for navigation items that have children.
 *
 * This component handles the logic for showing or hiding children in an accordion style.
 * The button changes appearance based on the `state` and `active` props.
 *
 * @param {Object} props - The properties for the component.
 * @param {string} props.title - The title of the navigation item.
 * @param {React.ReactNode} props.icon - The icon associated with the navigation item.
 * @param {'full' | 'dock'} [props.state='full'] - The state of the navigation (full or docked).
 * @param {boolean} [props.active] - Whether the navigation item is active.
 * @returns {JSX.Element} The rendered button for navigation items with children.
 */
const InternalNavItemWithChildrenButton = ({ title, icon, state = 'full', active, ExpandIcon }) =>
  state === 'full' ? (
    <Button
      {...defaultProps({ title })}
      as={AccordionButton}
      leftIcon={
        <HStack w={4} h={4}>
          {icon}
        </HStack>
      }
      w="100%"
    >
      {title}
      <ExpandIcon ml="auto" hidden={state !== 'full'} />
    </Button>
  ) : (
    <IconButton {...defaultProps({ title })} icon={icon} sx={active ? activeLinkStyles : {}} justifyContent="center" />
  )

InternalNavItemWithChildrenButton.propTypes = {
  ...sharedPropTypes,
  active: PropTypes.bool
}

/**
 * InternalNavItemWithChildren component renders a navigation item that has nested children.
 *
 * This component uses an accordion to handle nested navigation items, with logic to determine if the
 * item or its children should be expanded by default.
 *
 * @param {Object} props - The properties for the component.
 * @param {string} props.id - The unique identifier for the navigation item.
 * @param {string} props.title - The title of the navigation item.
 * @param {React.ReactNode} props.icon - The icon associated with the navigation item.
 * @param {'full' | 'dock'} [props.state='full'] - The state of the navigation (full or docked).
 * @param {Array<Object>} props.children - The nested children navigation items.
 * @returns {JSX.Element} The rendered navigation item with children.
 */
const InternalNavItemWithChildren = ({ id, title, icon, state = 'full', children }) => {
  const { pathname } = useLocation()

  const { parentExpandedByDefault, childExpandedByDefault } = useMemo(() => {
    const childrenPaths = children.map(child => child.navLink)
    const grandChildrenPaths = children
      .map(child => (child.children || []).map(grandChild => grandChild.navLink))
      .flat()

    const parentExpandedByDefault = pathname !== '/' && [...childrenPaths, ...grandChildrenPaths].includes(pathname)
    const childExpandedByDefault = pathname !== '/' && grandChildrenPaths.includes(pathname)

    return { parentExpandedByDefault, childExpandedByDefault }
  }, [children, pathname])

  return (
    <Accordion key={id} allowToggle defaultIndex={parentExpandedByDefault ? [0] : [1]} w="full">
      <AccordionItem border="none">
        {({ isExpanded }) => (
          <>
            <InternalNavItemWithChildrenButton
              title={title}
              icon={icon}
              state={state}
              active={parentExpandedByDefault}
              ExpandIcon={props => (
                <Box {...props}>
                  <FontAwesomeIcon
                    size="xs"
                    icon={byPrefixAndName['fad'][isExpanded ? 'chevron-down' : 'chevron-right']}
                  />
                </Box>
              )}
            />
            {state === 'full' && (
              <AccordionPanel py="0" px="2">
                <List mb="0">
                  {children.map(child => (
                    <Tooltip
                      key={child.id}
                      hidden={!!child.navLink}
                      label="Coming soon"
                      aria-label={child.title}
                      placement="right"
                    >
                      <ListItem key={child.id}>
                        {child.children && child.children.length > 0 ? (
                          <Accordion allowToggle defaultIndex={childExpandedByDefault ? [0] : [1]}>
                            <AccordionItem border="none">
                              {({ isExpanded: childIsExpanded }) => (
                                <>
                                  <InternalNavItemWithChildrenButton
                                    title={child.title}
                                    icon={child.icon}
                                    ExpandIcon={props => (
                                      <Box {...props}>
                                        <FontAwesomeIcon
                                          size="xs"
                                          icon={
                                            byPrefixAndName['fad'][childIsExpanded ? 'chevron-down' : 'chevron-right']
                                          }
                                        />
                                      </Box>
                                    )}
                                  />
                                  <AccordionPanel py="0" px="2">
                                    <List mb="0">
                                      {child.children.map(grandChild => (
                                        <ListItem key={grandChild.id}>
                                          <SidebarLeafNavItem item={grandChild} />
                                        </ListItem>
                                      ))}
                                    </List>
                                  </AccordionPanel>
                                </>
                              )}
                            </AccordionItem>
                          </Accordion>
                        ) : (
                          <SidebarLeafNavItem item={child} />
                        )}
                      </ListItem>
                    </Tooltip>
                  ))}
                </List>
              </AccordionPanel>
            )}
          </>
        )}
      </AccordionItem>
    </Accordion>
  )
}

InternalNavItemWithChildren.propTypes = {
  ...sharedPropTypes,
  children: PropTypes.arrayOf(
    PropTypes.shape({
      ...sharedPropTypes,
      navLink: PropTypes.string,
      children: PropTypes.arrayOf(
        PropTypes.shape({
          ...sharedPropTypes,
          navLink: PropTypes.string
        })
      )
    })
  )
}

/**
 * MFNavNavItem component determines the appropriate type of navigation item to render.
 *
 * Based on the provided props, this component will render either an internal or external link, and handle
 * whether the item has nested children that require an accordion structure.
 *
 * @param {Object} props - The properties for the component.
 * @param {string} props.id - The unique identifier for the navigation item.
 * @param {string} props.title - The title of the navigation item.
 * @param {React.ReactNode} props.icon - The icon associated with the navigation item.
 * @param {Object} props.link - The link configuration for the navigation item.
 * @param {Array<Object>} [props.children=[]] - The nested children navigation items.
 * @returns {JSX.Element} The rendered navigation item.
 */
export default function MFNavNavItem({ id, title, icon, link, children = [] }) {
  const {
    menu: {
      state: { trueDockState }
    }
  } = useMFNav()

  if (children.length > 0)
    return (
      <InternalNavItemWithChildren
        id={id}
        title={title}
        icon={icon}
        state={trueDockState ? 'dock' : 'full'}
        // eslint-disable-next-line react/no-children-prop
        children={children}
      />
    )

  if (link.type === 'external')
    return <ExternalNavItem id={id} title={title} icon={icon} href={link.url} state={trueDockState ? 'dock' : 'full'} />

  return (
    <InternalNavItemWithoutChildren
      id={id}
      title={title}
      icon={icon}
      to={link.url ?? ''}
      state={trueDockState ? 'dock' : 'full'}
    />
  )
}

MFNavNavItem.propTypes = {
  ...sharedPropTypes,
  link: PropTypes.shape({
    type: PropTypes.oneOf(['internal', 'external']).isRequired,
    url: PropTypes.string
  }).isRequired,
  children: PropTypes.arrayOf(
    PropTypes.shape({
      ...sharedPropTypes,
      navLink: PropTypes.string,
      children: PropTypes.arrayOf(
        PropTypes.shape({
          ...sharedPropTypes,
          navLink: PropTypes.string
        })
      )
    })
  )
}
